// src/components/Sidebar/FeaturedSection.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchFeaturedPosts } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { buildSubscriberSignInPath, isSubscriberOnlyContent } from '../../utils/subscriberAccess';
import { resolveImageUrl } from '../../utils/imageUrl';
import './FeaturedSection.css';

const sanitizeText = (text = '') => text.replace(/<[^>]+>/g, ' ');

const FeaturedSection = () => {
  const { canAccessExclusiveContent } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const response = await fetchFeaturedPosts();
        setItems(response || []);
      } catch (error) {
        console.error('Unable to load featured episodes', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadFeatured();
  }, []);

  const normalizedItems = useMemo(() => {
    return items.map((item) => ({
      id: item.id,
      title: item.title,
      slug: item.slug || item.id,
      duration: item.itunes_duration || item.duration,
      episodeNumber: item.episode_number || item.itunes_episode,
      description: sanitizeText(
        item.summary || item.description || item.subtitle || ''
      ).slice(0, 120),
      isLocked: isSubscriberOnlyContent(item),
      image:
        item.thumbnail_url ||
        item.itunes_image ||
        item.hero_thumbnail ||
        (item.media_metadata && item.media_metadata[0]?.url) ||
        null,
    }));
  }, [items]);

  if (loading) {
    return (
      <section className="featured-card featured-card--skeleton" aria-hidden="true" />
    );
  }

  if (!normalizedItems.length) {
    return null;
  }

  const activeItem = normalizedItems[activeIndex];
  const activeDestination =
    activeItem.isLocked && !canAccessExclusiveContent
      ? buildSubscriberSignInPath(`/episode/${activeItem.slug}`)
      : `/episode/${activeItem.slug}`;

  return (
    <section className="featured-card" aria-label="Featured show">
      <div className="featured-card__gradient" aria-hidden="true" />
      <div className="featured-card__content">
        <span className="featured-card__eyebrow">Featured Show</span>
        <h3 className="featured-card__title">{activeItem.title}</h3>
        {activeItem.episodeNumber && (
          <p className="featured-card__meta">
            Episode {activeItem.episodeNumber}
            {activeItem.duration && <span>• {activeItem.duration}</span>}
          </p>
        )}
        <p className="featured-card__description">
          {activeItem.description || 'Fresh conversations from the GUHSO community.'}
        </p>
        <Link to={activeDestination} className="featured-card__cta">
          Listen Now
          <i className="fas fa-arrow-right" aria-hidden="true" />
        </Link>
      </div>
      {activeItem.image ? (
        <div className="featured-card__media">
          <img src={resolveImageUrl(activeItem.image)} alt="Featured artwork" loading="lazy" />
        </div>
      ) : (
        <div className="featured-card__media featured-card__media--placeholder">
          <i className="fas fa-podcast" aria-hidden="true" />
        </div>
      )}
      <div className="featured-card__dots" role="tablist" aria-label="Featured episode picker">
        {normalizedItems.map((item, index) => (
          <button
            key={item.id || index}
            type="button"
            className={`featured-card__dot ${index === activeIndex ? 'active' : ''}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Show featured episode: ${item.title}`}
            aria-pressed={index === activeIndex}
          />
        ))}
      </div>
    </section>
  );
};

export default FeaturedSection;
